import React, {KeyboardEvent, ReactNode} from 'react';
import {borderRadius, colors, fontFamily} from '@/styles/tokens';

/**
 * Chip — the app's reusable pill / label primitive (replaces MUI `Chip`).
 *
 * It owns the shared structural grammar only (layout, size, shape, label
 * structure, and the removable behaviour). Visual identity — colour, background,
 * border, feature padding, typography and animations — is supplied by consumers
 * through `className`, exactly as `ScrollableTabs` delegates look via
 * `tabClassName`. Non-removable chips render a plain, non-interactive `<span>`;
 * any navigation/click behaviour is provided by a wrapping `next/link`, as it
 * was with MUI.
 */

// Design tokens injected as CSS custom properties (consumed by the `.chip-*`
// global classes in src/styles/globals.scss). Single source of truth is
// src/styles/tokens.ts. Defined once at module scope — never recreated per render.
const chipVars = {
    '--chip-radius': borderRadius.xl,
    '--chip-color': colors.text.primary,
    '--chip-font': fontFamily.body,
    '--chip-focus': colors.primary.main
} as React.CSSProperties;

export interface ChipProps {
    /** Label content. `ReactNode` (not `string`) so parsed HTML works (html-react-parser). */
    children: ReactNode;
    /** Consumer-owned visual identity: colour, background, border, feature padding, etc. */
    className?: string;
    /**
     * Consumer-owned inline overrides, layered over the token CSS variables on the
     * root. Inline styles outrank the `.chip-root` base (which uses no `!important`),
     * so feature-specific look (fill, border, padding) can be supplied here as a
     * component-local JS style object instead of a co-located SCSS module.
     */
    style?: React.CSSProperties;
    /** Leading media (avatar / icon) rendered flush to the chip's left edge. */
    leading?: ReactNode;
    /**
     * When provided, the chip becomes removable: a remove control is shown and
     * Backspace/Delete on the focused chip triggers it (parity with MUI Chip).
     */
    onRemove?: () => void;
    /** Accessible name for the remove control (e.g. "Remove Healthcare"). Set whenever `onRemove` is. */
    removeLabel?: string;
}

// Material's "close" glyph, inlined so a single icon does not pull in
// @mui/icons-material. Decorative (aria-hidden) — the button carries the name.
const CloseGlyph = () => (
    <svg className="chip-remove-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
);

function Chip({children, className = '', style, leading, onRemove, removeLabel}: Readonly<ChipProps>) {
    const removable = typeof onRemove === 'function';

    const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
        // Mirror MUI: Backspace/Delete on the focused chip removes it.
        if (event.key === 'Backspace' || event.key === 'Delete') {
            event.preventDefault();
            onRemove?.();
        }
    };

    const rootClassName = ['chip-root', removable ? 'chip-removable' : '', className].filter(Boolean).join(' ');

    return (
        <span
            className={rootClassName}
            // Token vars first, then consumer overrides — consumer keys never clobber the `--chip-*` vars.
            style={{...chipVars, ...style}}
            tabIndex={removable ? 0 : undefined}
            onKeyDown={removable ? handleKeyDown : undefined}
        >
            {leading ? <span className="chip-leading">{leading}</span> : null}
            <span className="chip-label">{children}</span>
            {removable ? (
                <button type="button" className="chip-remove" aria-label={removeLabel} tabIndex={-1} onClick={() => onRemove?.()}>
                    <CloseGlyph />
                </button>
            ) : null}
        </span>
    );
}

export default Chip;
